/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';

import { BuilderPermission, JobFinancialsRestService, JobListFilterDto, JobRestService, SystemRoleBit } from '@gh/builder-shared-data';
import { InitViewService } from '@gh/common-shared';
import { SlicedDataSource } from '@gh/core-data';
import { GridLayoutService } from '@gh/core-ui-grid';

import { JobListItem } from './job.model';
import { BuilderUserContext, FilterNames, FilterRestService } from '@gh/builder-shared';
import { ENV_BASE_URL } from '@gh/config';

@Injectable()
export class JobFinancialsListBusinessService implements InitViewService {
  COLUMN_ENABLE_CONDITION = {
    estimatedProfit: (): boolean => this.getPermissionForPrices(),
    profitMargin: (): boolean => this.getPermissionForPrices(),
    originalBudget: (): boolean => this.getPermissionForPrices(),
    originalContractAmount: (): boolean => this.getPermissionForPricesExtended(),
    originalProfitMargin: (): boolean => this.getPermissionForPrices(),
    currentBudget: (): boolean => this.getPermissionForPrices(),
    drawsMadeWithGst: (): boolean => this.getPermissionForPricesExtended(),
    totalInvoices: (): boolean => this.getPermissionForPricesExtended(),
    actualProfit: (): boolean => this.getPermissionForPrices(),
    totalAmount: (): boolean => this.getCountry() !== 'us',
  };

  constructor(private jobFinancialsRest: JobFinancialsRestService,
              private jobRest: JobRestService,
              private userContext: BuilderUserContext,
              private filterService: FilterRestService,
              private gridLayoutService: GridLayoutService) {

  }

  initView(route: ActivatedRouteSnapshot): any {
    return {
      gridLayout: this.gridLayoutService.get('jobFinancialsList', this.COLUMN_ENABLE_CONDITION),
      filter:  this.filterService.get(FilterNames.jobFinancialsList),
    };
  }

  findAllAsDataSource(filter: JobListFilterDto): SlicedDataSource<JobListItem> {
    return this.jobFinancialsRest.findAllAsDataSource(filter);
  }

  getCountry(): string {
    const baseUrl = ENV_BASE_URL.toString();
    // @ts-ignore
    switch (true) {
      case baseUrl.includes('-au') || baseUrl.includes('.au'):
        return 'au';
      case baseUrl.includes('-nz') || baseUrl.includes('.nz'):
        return 'nz';
      case baseUrl.includes('-us') || baseUrl.includes('.com'):
        return 'us';
      default:
        return 'us';
    }
  }

  getPermissionForPrices(): boolean {
    return this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      || this.userContext.hasSystemRole(SystemRoleBit.Master)
      || this.userContext.hasPermission(BuilderPermission.Estimating.JobEstimate.EDIT)
      || this.userContext.hasPermission(BuilderPermission.Estimating.JobEstimate.VIEW)
      || this.userContext.hasPermission(BuilderPermission.JobAdmin.Financials.VIEW);
  }

  getPermissionForPricesExtended(): boolean {
    return this.getPermissionForPrices()
      || this.userContext.hasPermission(BuilderPermission.Accounting.Draws.VIEW)
      || this.userContext.hasPermission(BuilderPermission.Accounting.Draws.PRODUCE);
  }
}
